import React from 'react';

const CATEGORY_TO_BODY = {
  'Ring': 'hand',
  'Bangle': 'wrist',
  'Bracelet': 'wrist',
  'Chain': 'neck',
  'Necklace': 'neck',
  'Pendant': 'neck',
  'Mangalsutra': 'neck',
  'Earring': 'ear',
  'Nose Pin': 'nose',
  'Anklet': 'ankle',
};

const FILL = '#F5E6D3';
const STROKE = '#D4C5B0';

const BODY_CONFIGS = {
  hand: {
    label: 'On Hand',
    viewBox: '0 0 200 300',
    realWidthMm: 85,
    jewelryCenter: { x: 121, y: 110 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M72,295 L72,145 Q72,128 105,128 Q138,128 138,145 L138,295 Z" />
        <path d="M72,160 Q64,138 58,108 Q54,78 62,70 Q70,63 76,78 Q80,98 82,128 Z" />
        <path d="M80,132 L83,52 Q85,36 92,36 Q96,36 97,52 L98,132 Z" />
        <path d="M98,132 L101,38 Q103,20 110,20 Q114,20 115,38 L116,132 Z" />
        <path d="M116,132 L118,46 Q120,30 127,30 Q131,30 132,46 L131,132 Z" />
        <path d="M131,138 L132,70 Q133,56 140,56 Q143,56 143,70 L141,142 Z" />
      </g>
    ),
  },
  wrist: {
    label: 'On Wrist',
    viewBox: '0 0 180 300',
    realWidthMm: 72,
    jewelryCenter: { x: 90, y: 192 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M48,0 L48,160 Q48,178 54,190 Q47,198 46,210 Q48,226 56,238 L56,300 L124,300 L124,238 Q132,226 134,210 Q133,198 126,190 Q132,178 132,160 L132,0 Z" />
      </g>
    ),
  },
  neck: {
    label: 'On Neck',
    viewBox: '0 0 300 250',
    realWidthMm: 340,
    jewelryCenter: { x: 150, y: 165 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M5,250 Q28,192 92,158 Q122,145 132,112 L132,58 Q138,26 150,14 Q162,26 168,58 L168,112 Q178,145 208,158 Q272,192 295,250 Z" />
      </g>
    ),
  },
  ear: {
    label: 'On Ear',
    viewBox: '0 0 130 180',
    realWidthMm: 38,
    jewelryCenter: { x: 68, y: 152 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M55,18 Q82,4 108,22 Q120,42 120,72 Q120,98 110,118 Q100,134 86,144 Q74,150 66,148 Q58,144 58,136 Q58,128 66,116 Q76,100 80,78 Q82,58 72,40 Q62,28 52,32 Q44,38 48,52" />
      </g>
    ),
  },
  nose: {
    label: 'On Nose',
    viewBox: '0 0 140 150',
    realWidthMm: 42,
    jewelryCenter: { x: 38, y: 118 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M70,6 Q66,28 56,56 Q48,80 40,98 Q33,110 33,120 Q33,132 48,134 L70,130 L92,134 Q107,132 107,120 Q107,110 100,98 Q92,80 84,56 Q74,28 70,6 Z" />
        <ellipse cx="50" cy="122" rx="9" ry="5" fill="none" stroke={STROKE} strokeWidth="0.8" />
        <ellipse cx="90" cy="122" rx="9" ry="5" fill="none" stroke={STROKE} strokeWidth="0.8" />
      </g>
    ),
  },
  ankle: {
    label: 'On Ankle',
    viewBox: '0 0 180 300',
    realWidthMm: 80,
    jewelryCenter: { x: 88, y: 250 },
    Silhouette: () => (
      <g fill={FILL} stroke={STROKE} strokeWidth="1.2">
        <path d="M55,0 L55,212 Q55,232 50,246 Q44,260 48,274 Q56,286 78,294 Q98,300 124,296 Q140,290 148,276 Q152,262 146,248 Q138,234 132,218 Q128,202 128,192 L128,0 Z" />
      </g>
    ),
  },
};

export default function BodyOverlayView({ modelCategory, widthMm, heightMm, productImage, productName }) {
  const bodyType = CATEGORY_TO_BODY[modelCategory];
  if (!bodyType) return null;

  const config = BODY_CONFIGS[bodyType];
  if (!config) return null;

  const w = widthMm || 0;
  const h = heightMm || 0;
  if (w <= 0 && h <= 0) return null;

  const vbParts = config.viewBox.split(' ').map(Number);
  const vbW = vbParts[2];
  const vbH = vbParts[3];

  const maxDisplayW = 240;
  const displayScale = maxDisplayW / vbW;
  const displayH = vbH * displayScale;

  const pxPerMm = maxDisplayW / config.realWidthMm;

  const prodDisplayW = Math.max(w * pxPerMm, 36);
  const prodDisplayH = Math.max((h || w) * pxPerMm, 36);

  const jx = config.jewelryCenter.x * displayScale;
  const jy = config.jewelryCenter.y * displayScale;

  const aspectRatio = prodDisplayW / prodDisplayH;
  const borderRadius = aspectRatio > 0.8 && aspectRatio < 1.2 ? '50%' : '30%';

  return (
    <div className="flex flex-col items-center" data-testid="body-overlay-view">
      <div className="relative" style={{ width: maxDisplayW, height: displayH }}>
        <svg
          viewBox={config.viewBox}
          width={maxDisplayW}
          height={displayH}
          className="absolute inset-0"
        >
          <config.Silhouette />
        </svg>

        <div
          className="absolute overflow-hidden"
          style={{
            left: jx - prodDisplayW / 2,
            top: jy - prodDisplayH / 2,
            width: prodDisplayW,
            height: prodDisplayH,
            borderRadius,
            border: '2px solid #C9A967',
            boxShadow: '0 2px 10px rgba(201, 169, 103, 0.35)',
          }}
          data-testid="body-overlay-product"
        >
          {productImage ? (
            <img
              src={productImage}
              alt={productName || 'Product'}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full bg-[#C9A967]/20 flex items-center justify-center">
              <span className="text-[8px] text-[#8B7355] font-serif">Product</span>
            </div>
          )}
        </div>

        <div
          className="absolute text-center pointer-events-none"
          style={{
            left: jx - 40,
            top: jy + prodDisplayH / 2 + 5,
            width: 80,
          }}
        >
          <span className="text-[10px] text-[#8B7355] whitespace-nowrap bg-white/80 px-1.5 py-0.5 rounded">
            {w > 0 && `${w}mm`}{w > 0 && h > 0 && ' × '}{h > 0 && `${h}mm`}
          </span>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3 font-serif">{config.label}</p>
    </div>
  );
}

export { CATEGORY_TO_BODY };
